import { t } from '@lingui/core/macro';
import { BookingRecordListForCustomer } from '@navio/nsdk';
import { PaginatedResponse } from 'noddi-types';
import { NoddiBasicCard, NoddiEmptyPage, TablePagination } from 'noddi-ui-web';
import { datesSortCompare } from 'noddi-util';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { BookingListItem } from './BookingListItem';

type Props = {
  bookings: PaginatedResponse<BookingRecordListForCustomer[]>;
  isFetching: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
    search?: string;
  };
  onPaginationChange: (params: { pageIndex: number; pageSize: number; search?: string }) => void;
  emptyStateMessage?: string;
};

export const BookingsList = ({ bookings, isFetching, pagination, onPaginationChange, emptyStateMessage }: Props) => {
  if (bookings.count === 0) {
    return (
      <NoddiEmptyPage
        className='rounded-xl bg-card'
        title={t`No bookings`}
        message={emptyStateMessage ?? t`We couldn't find any bookings for you.`}
      />
    );
  }

  const bookingsSortedByUnpaidFirst = [...bookings.results].sort((a, b) => {
    // Prioritize unpaid completed bookings that are not invoiced
    const aPriority = a.completedAt && !a.order.isFullyPaid && !a.order.isManuallyInvoiced;
    const bPriority = b.completedAt && !b.order.isFullyPaid && !b.order.isManuallyInvoiced;

    if (aPriority && !bPriority) {
      return -1;
    }
    if (!aPriority && bPriority) {
      return 1;
    }

    return datesSortCompare(a.date, b.date, 'desc');
  });

  return (
    <div className='flex w-full flex-col gap-2'>
      {bookingsSortedByUnpaidFirst.map((booking) => (
        <BookingListItem key={booking.id} booking={booking} />
      ))}
      <NoddiBasicCard className='p-4'>
        <TablePagination
          loading={isFetching}
          pagination={pagination}
          onChange={onPaginationChange}
          totalCount={bookings.count}
          translations={getCommonTranslations().table}
        />
      </NoddiBasicCard>
    </div>
  );
};
